<template>
    <teleport to="body">

        <div id="modal-create-dataset">

            <div class="rp-overlay h-full"></div>

            <div class="rp-modal">
                
                <div v-if="!layout.loading">

                    <div class="rp-modal-header">
                        <h3 class="rp-modal-title">{{ $t('New dataset') }}</h3>
                        <button class="rp-btn-dismiss"
                            @click="$emit('closeModal')">
                            <XIcon class="w-5 h-5" />
                        </button>
                    </div>

                    <div class="bg-white p-5">
                        <form ref="dataset" class="validate-form" @submit.prevent="createDataset($event)">
                            
                            <div class="relative text-gray-700 dark:text-gray-300">
                                <div class="text-normal mb-2 font-bold">{{ $t('Dataset name') }} *</div>
                                <input type="text" name="dsname" class="form-control py-3 px-4   pr-10 " v-model="layout.dataset.name" required />
                                <div class="text-xs pt-2 pl-2">{{ $t('Use up to 32 characters, which can include letters, numbers, and underscores') }}.</div>
                            </div>

                            <div class="grid chat grid-cols-12 gap-5 px-2 text-gray-600 p-3">
                                
                                <div class="col-span-6 xl:col-span-3 bg-gray-200 rounded p-2 text-left">
                                    <img :src="require(`@/assets/images/rosepetal/icon/dataset_class.png`)" class="w-full" />
                                    <div class="text-lg mb-3 font-medium text-center text-gray-900 mt-3">{{ $t('Classification') }}</div>
                                    <div class="text-xs text-center mx-5 pb-4 text-gray-900 h-16">
                                        {{ $t('Images are assigned to tags') }}.
                                    </div>
                                    <div class="pt-2 pb-3 text-center">
                                        <button type="submit" @click="layout.dataset.type='MULTICLASS';" class="btn w-44 bg-green-600 text-white zoom-in font-normal">{{ $t('Create') }}</button> 
                                    </div>    
                                </div>

                                <div class="col-span-6 xl:col-span-3 bg-gray-200 rounded p-2 text-left">
                                    <img :src="require(`@/assets/images/rosepetal/icon/dataset_anm.png`)" class="w-full" />
                                    <div class="text-lg mb-3 font-medium text-center text-gray-900 mt-3">{{ $t('Anomaly detection') }}</div>
                                    <div class="text-xs text-center mx-5 pb-4 text-gray-900 h-16">
                                        {{ $t('Images are marked as normal or defective') }}.
                                    </div>
                                    <div class="pt-2 pb-3 text-center">
                                        <button type="submit" @click="layout.dataset.type='ANOMALY';" class="btn w-44 bg-green-600 text-white zoom-in font-normal">{{ $t('Create') }}</button> 
                                    </div>
                                </div>

                                <div class="col-span-6 xl:col-span-3 bg-gray-200 rounded p-2 text-left">
                                    <img :src="require(`@/assets/images/rosepetal/icon/dataset_obj.png`)" class="w-full" />
                                    <div class="text-lg mb-3 font-medium text-center text-gray-900 mt-3">{{ $t('Object detection') }}</div>
                                    <div class="text-xs text-center mx-5 pb-4 text-gray-900 h-16">
                                        {{ $t('Delimiting rectangles are drawn around the objects to be detected') }}.
                                    </div>
                                    <div class="pt-2 pb-3 text-center">
                                        <button type="submit" @click="layout.dataset.type='imageObjectDetection'" class="btn w-44 bg-green-600 text-white zoom-in font-normal">{{ $t('Create') }}</button> 
                                    </div>
                                </div>

                                <div class="col-span-6 xl:col-span-3 bg-gray-200 rounded p-2 text-left">
                                    <img :src="require(`@/assets/images/rosepetal/icon/dataset_segm.png`)" class="w-full" />
                                    <div class="text-lg mb-3 font-medium text-center text-gray-900 mt-3">{{ $t('Image segmentation') }}</div>
                                    <div class="text-xs text-center mx-5 pb-4 text-gray-900 h-16">
                                        {{ $t('Defective areas are marked on each image') }}.
                                    </div>
                                    <div class="pt-2 pb-3 text-center">
                                        <button type="submit" @click="layout.dataset.type='MULTILABEL'" class="btn w-44 bg-green-600 text-white zoom-in font-normal">{{ $t('Create') }}</button> 
                                    </div>
                                </div>

                                <input type="hidden" name="dstype" :value="layout.dataset.type" />

                            </div>

                        </form>
                    </div>

                </div>

                <div v-else class="intro-y col-span-12 lg:col-span-12 flex flex-col justify-center items-center mt-12 p-10 pt-5 bg-white">
                    <div class="flex items-center justify-center mt-5"><img :src="require(`@/assets/images/rosepetal/icon/logoLoadingGray.png`)" class="w-20" /></div>
                    <LoadingIcon icon="three-dots" class="w-10 h-10" />
                </div>

            </div>
            
        </div>

    </teleport>
</template>

<script setup>
import { onMounted, ref }   from "vue";
import { helper as $h } 	from "@/utils/helper";
import * as rosepetalModel  from "rosepetal-model";

const $dataset    = rosepetalModel.default.dataset;
const $model      = rosepetalModel.default.model;

const emit = defineEmits(['datasetCreated']);

const layout = ref({
    loading:    false,
    dataset:    { name: '', type: '' }
});

const createDataset = async () => {
    layout.value.loading = true;
    if (layout.value.dataset.name && layout.value.dataset.type) {
        await $dataset.create({ name: layout.value.dataset.name, type: layout.value.dataset.type }).then(async response => {
            if (response.datasetID) {
                await $model.createVertex(response.datasetID);
                $h.NotificationTxt({ text: "Dataset successfully created"});
                emit('datasetCreated');
            }
        });
    }
}

onMounted(() => {
    let currentDate = new Date();
    layout.value.dataset.name = "untitled_" + currentDate.getTime();
});
</script>